import {defineStore} from 'pinia'
import {ref} from 'vue'
import {useCurrentAppPlatform} from '@toolify/client/src/modules/platform/composables/useCurrentAppPlatform'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {PushNotifications, RegistrationError, Token, ActionPerformed} from '@capacitor/push-notifications'
import {App, AppState} from '@capacitor/app'
import {Capacitor} from '@capacitor/core'
import {RouteLocationNormalized, useRouter} from 'vue-router'
import {NotificationTypeItemMap} from '@toolify/client/src/stores/NotificationStore/maps/NotificationTypeItemMap'
import {INotificationResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {useAppStore} from '../AppStore/useAppStore'
import {useAuthStore} from '../AuthStore/useAuthStore'
import {useCurrentSessionApiStore} from '../api/CurrentSessionApiStore/useCurrentSessionApiStore'
import {useNotificationApiStore} from '../api/NotificationApiStore/useNotificationApiStore'
import {useI18n} from 'vue-i18n'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'

export const useMobileNotificationsStore = defineStore('mobileNotifications', () => {
  const recentLinkToNavigate = ref<RouteLocationNormalized>(null)
  const {t} = useI18n()
  const messages = useMessages()
  const currentDeviceToken = ref<string>(null)
  const {currentAppPlatformType} = useCurrentAppPlatform()
  const hasPermission = ref<boolean>(false)
  const router = useRouter()
  const appStore = useAppStore()
  const authStore = useAuthStore()
  const currentSessionApiStore = useCurrentSessionApiStore()
  const notificationApiStore = useNotificationApiStore()
  
  function initialize() {
    if (currentAppPlatformType !== AppPlatformType.Mobile) {
      return
    }
    PushNotifications.addListener('registration', onRegistration)
    PushNotifications.addListener('registrationError', onRegistrationError)
    PushNotifications.addListener('pushNotificationActionPerformed', onPushActionPerformed)
    App.addListener('appStateChange', onAppStateChange)
    checkNotificationPermission()
  }

  function dispose() {
    if (currentAppPlatformType !== AppPlatformType.Mobile) {
      return
    }
    PushNotifications.removeAllListeners()
    App.removeAllListeners()
  }

  function onAppStateChange(state: AppState) {
    if (!state.isActive) {
      return
    }
    checkNotificationPermission()
  }

  function onRegistration(token: Token) {
    currentDeviceToken.value = token.value
  }

  function onRegistrationError(error: RegistrationError) {
    // TODO: Handle registration error
  }

  async function onPushActionPerformed(action: ActionPerformed) {
    try {
      const notificationId = action.notification?.data?.notificationId
      if (!notificationId) {
        return
      }

      const notification = await notificationApiStore.getNotification(notificationId)
      if (!notification || !notification.type) {
        return
      }

      await notificationApiStore.markNotificationAsRead(notificationId)
      routeNotification(notification)
    } catch (error) {
      messages.error(t('mobile.notifications.routingError'))
      // eslint-disable-next-line no-console
      console.error('Failed to handle push notification:', error)
    }
  }

  function routeNotification(notification: INotificationResponseInstance<NotificationType>) {
    const notificationHandler = NotificationTypeItemMap.get(notification.type)
    if (!notificationHandler) {
      // eslint-disable-next-line no-console
      console.warn('No notification handler for type', notification.type)
      return
    }

    let notificationItem: ReturnType<typeof notificationHandler> | null
    try {
      notificationItem = notificationHandler(notification)
    } catch (error) {
      messages.error(t('mobile.notifications.routingError'))
      // eslint-disable-next-line no-console
      console.error('Failed to route notification:', error)
      return
    }

    if (!notificationItem || !notificationItem.to) {
      messages.error(t('mobile.notifications.routingError'))
      // eslint-disable-next-line no-console
      console.warn('Notification has no navigation target', notification.type)
      return
    }

    if (appStore.isPageLoading) {
      recentLinkToNavigate.value = notificationItem.to as RouteLocationNormalized
    } else {
      void router.push(notificationItem.to)
    }
  }

  async function checkNotificationPermission(): Promise<boolean> {
    if (currentAppPlatformType !== AppPlatformType.Mobile) {
      return false
    }
    const permission = await PushNotifications.checkPermissions()
    const isGranted = permission.receive === 'granted'
    hasPermission.value = isGranted
    if (isGranted && !currentDeviceToken.value) {
      await register()
    }
    return isGranted
  }

  async function requestNotificationPermission(): Promise<boolean> {
    if (currentAppPlatformType !== AppPlatformType.Mobile) {
      return false
    }

    try {
      const permission = await PushNotifications.requestPermissions()

      if (permission.receive === 'denied') {
        showManualEnableGuide()
        hasPermission.value = false
        return false
      }

      hasPermission.value = permission.receive === 'granted'
      if (hasPermission.value) {
        await register()
      }
      return hasPermission.value
    } catch (error) {
      hasPermission.value = false
      return false
    }
  }

  function showManualEnableGuide() {
    const platform = Capacitor.getPlatform()
    
    const guideKeys: Record<string, string> = {
      ios: 'mobile.notifications.iosGuide',
      android: 'mobile.notifications.androidGuide',
    }

    const title = t('mobile.notifications.title')
    const messageKey = guideKeys[platform] ?? 'mobile.notifications.defaultGuide'
    const message = t(messageKey)

    alert(`${title}\n\n${message}`)
  }

  async function register(): Promise<void> {
    if (currentAppPlatformType !== AppPlatformType.Mobile) {
      return
    }
    if (!hasPermission.value) {
      return
    }
    await PushNotifications.register()
  }

  return {
    recentLinkToNavigate,
    currentDeviceToken,
    hasPermission,
    initialize,
    dispose,
    requestNotificationPermission,
    checkNotificationPermission,
    register,
    routeNotification,
  }
})
